import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProductList } from '@/hooks/useProductList';
import { openSaleSmartlyChat } from '@/lib/salesmartly';
import { groupProductsByCategory } from '@/utils/product-category';

const allServicesLink = { label: '查看全部 →', path: '/services', highlight: true };

const helpLinks = [
  { label: '如何下单', path: '/help/how-to-order' },
  { label: '如何付款', path: '/help/how-to-pay' },
  { label: '查看结果', path: '/help/how-to-view-result' },
  { label: '联系我们', path: '/contact' },
  { label: '推广赚钱', path: '/affiliate' },
];

export default function Footer() {
  const { products } = useProductList();

  const queryServices = useMemo(() => {
    const categoryFirstProducts = groupProductsByCategory(products)
      .flatMap((group) => {
        const product = group.products[0];
        if (!product) {
          return [];
        }
        return [
          {
            label: product.productName,
            path: `/services/${product.productCode}`,
            highlight: false,
          },
        ];
      });

    return [...categoryFirstProducts.slice(0, 5), allServicesLink];
  }, [products]);

  // “扫码访问手机版”二维码：动态生成指向当前站点根域名的二维码
  // 这里使用外部 QR 服务直接返回图片（避免引入 node/npm 依赖）。
  const qrSrc = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const originUrl = `${window.location.origin}/`;
    const apiUrl = 'https://api.qrserver.com/v1/create-qr-code/';
    const size = '160x160';
    return `${apiUrl}?size=${size}&data=${encodeURIComponent(originUrl)}`;
  }, []);

  return (
    <footer className="bg-gray-900 text-white pb-16 md:pb-0">
      {/* Top gradient line */}
      <div className="h-0.5 bg-gradient-to-r from-blue-500 via-blue-400 to-blue-600" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Mobile footer */}
        <div className="md:hidden">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <img
                src="/new_logo.png"
                alt="查191"
                className="h-12 w-auto object-contain"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6 text-sm">
            <Link to="/services" className="text-gray-400 hover:text-white py-1.5">全部服务</Link>
            <Link to="/help" className="text-gray-400 hover:text-white py-1.5">帮助中心</Link>
            <button type="button" onClick={() => openSaleSmartlyChat()} className="text-left text-gray-400 hover:text-white py-1.5">联系我们</button>
            <Link to="/affiliate" className="text-gray-400 hover:text-white py-1.5">推广赚钱</Link>
          </div>

          <p className="text-xs text-gray-500 mb-4 leading-relaxed">
            本网站服务仅供合法用途，用户需遵守当地法律法规。
          </p>

          <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-800">
            <span>© 2026 查191 v3.8.0</span>
            <div className="flex gap-4">
              <Link to="/help?tab=contact" className="hover:text-gray-300">隐私</Link>
              <Link to="/help?tab=contact" className="hover:text-gray-300">条款</Link>
            </div>
          </div>
        </div>

        {/* Desktop footer */}
        <div className="hidden md:block">
          <div className="grid grid-cols-4 gap-8 mb-8">
            {/* Brand col */}
            <div className="col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <img
                  src="/new_logo.png"
                  alt="查191"
                  className="h-14 w-auto object-contain"
                />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                中文用户第一大选择的全网信息查询平台，基于AI技术提供专业的信息查询服务。
              </p>
              <div className="flex items-center space-x-4">
                <div className="bg-white p-2 rounded-lg w-28 h-20 flex items-center justify-center">
                  <img
                    src={qrSrc}
                    alt="二维码"
                    className="h-14 w-auto object-contain"
                  />
                </div>
                <div className="text-xs text-gray-400">
                  <p className="text-gray-300 mb-1">扫码访问手机版</p>
                </div>
              </div>
            </div>

            {/* Query services */}
            <div>
              <h3 className="font-semibold mb-4 text-white text-sm">查询服务</h3>
              <ul className="space-y-2 text-sm">
                {queryServices.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={
                        item.highlight
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-gray-400 hover:text-blue-400'
                      }
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Help */}
            <div>
              <h3 className="font-semibold mb-4 text-white text-sm">帮助与支持</h3>
              <ul className="space-y-2 text-sm">
                {helpLinks.map((item) => (
                  <li key={item.path}>
                    {item.path === '/contact' ? (
                      <button type="button" onClick={() => openSaleSmartlyChat()} className="text-gray-400 hover:text-blue-400">
                        {item.label}
                      </button>
                    ) : (
                      <Link to={item.path} className="text-gray-400 hover:text-blue-400">
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-xs text-gray-500 text-center py-4 border-t border-gray-800 mb-4">
            本网站提供的所有技术服务仅供合法用途使用，用户在使用本服务时必须遵守当地法律法规。
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>© 2026 查191. 保留所有权利. v3.8.0</span>
            <div className="flex gap-6">
              <Link to="/help?tab=contact" className="hover:text-blue-400 cursor-pointer">隐私政策</Link>
              <Link to="/help?tab=contact" className="hover:text-blue-400 cursor-pointer">服务条款</Link>
              <Link to="/help" className="hover:text-blue-400 cursor-pointer">帮助中心</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
